import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardHeader,
  Button,
  Badge,
  Accordion,
  AccordionTrigger,
  AccordionContent,
  AccordionItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Clock,
  Stethoscope,
  List,
  Calculator,
  ArrowLeft,
  Calendar,
  User,
  Plus,
  RefreshCw,
  Info,
  AlertCircle,
  Loader2,
  Upload,
  Archive,
  Command,
  ChevronDown,
} from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { useCoding } from '../hooks/useCoding';
import { parseISOToLocalDate } from '../utils/dateUtils';
import {
  downloadPdf,
  recalculatePaymentInfo,
  rerunCodingDiagnosis,
} from '../services/rogerDocApi';
import PatientInformationSection from '../components/PatientInformationSection';
import MedicalDataModal from '../components/MedicalDataModal';
import CardTitleWithIcon from '../components/CardTitleWithIcon';
import ClickableTextWithModals from '../components/ClickableTextWithModals';
import PostCodingChecksCard from '../components/PostCodingChecksCard';
import DiagnosisCard from '../components/DiagnosisCard';
import SortableDiagnosisCard from '../components/SortableDiagnosisCard';
import DraggableGrid from '../components/DraggableGrid';
import CaseMixCard from '../components/CaseMixCard';
import AddDiagnosisModal from '../components/AddDiagnosisModal';
import DiagnosisStateManager from '../components/DiagnosisStateManager';
import PushToEmrCard from '../components/PushToEmrCard';
import { CodingActionsMenu } from '../components/CodingActionsMenu';
import type {
  DiagnosisWithReasoning,
  OEType,
  SeverityType,
} from '../types/codingData.types';

const CodingDiagnosis: React.FC = () => {
  const { showSuccess, showError, hasPermission } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );
  const navigate = useNavigate();
  const location = useLocation();

  // Use the coding hook for data management
  const coding = useCoding();
  const {
    responseData,
    loading,
    selectedVisit,
    setResponseData,
    lastSavedState,
    activeStateId,
    getAllStates,
    handleSelectState,
    handleStateUpdated,
    handleRefresh,
    isPolling,
    processingStatus,
    getDiagnosisId,
    urlDiagnosisId,
    hasUnsavedChanges,
  } = coding;

  const [rerunModalOpen, setRerunModalOpen] = useState(false);
  const [rerunLoading, setRerunLoading] = useState(false);
  const [rerunResult, setRerunResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleRerunClick = () => {
    setRerunResult(null);
    setRerunModalOpen(true);
  };

  const handleConfirmRerun = async () => {
    const diagnosisId = getDiagnosisId();
    if (!diagnosisId) return;

    setRerunLoading(true);
    try {
      const result = await rerunCodingDiagnosis(diagnosisId);
      if (result.success) {
        setRerunModalOpen(false);
        setRerunResult(null);
        showSuccess('Rerun initiated successfully');
        navigate('/coding');
      } else {
        setRerunResult({ success: false, message: result.message });
      }
    } catch {
      setRerunResult({
        success: false,
        message: 'An error occurred while communicating with the server.',
      });
    } finally {
      setRerunLoading(false);
    }
  };

  const handleCloseRerunModal = () => {
    setRerunModalOpen(false);
    setRerunResult(null);
  };

  // Check if user has permission to access Coding
  if (!hasPermission('coding:access')) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          <AlertDescription className='text-red-800'>
            You don't have permission to access Medical Coding features. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // UI state (modals, editing)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    title: string;
    dataType: string;
    data: unknown;
  } | null>(null);
  const [addDiagnosisModalOpen, setAddDiagnosisModalOpen] = useState(false);
  const [editingDiagnosisIndex, setEditingDiagnosisIndex] = useState<
    number | null
  >(null);
  const [editingPrimaryDiagnosis, setEditingPrimaryDiagnosis] = useState(false);
  const [alternativePrimaryCodesOpen, setAlternativePrimaryCodesOpen] =
    useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Helper function to recalculate and update case mix
  const recalculateAndUpdate = async (
    primaryDxName: string,
    secondaryDxNames: string[]
  ) => {
    const diagnosisId = getDiagnosisId();
    if (!diagnosisId) return;

    const caseMix = await recalculatePaymentInfo(
      diagnosisId,
      primaryDxName,
      secondaryDxNames
    );
    setResponseData(prev =>
      prev ? { ...prev, case_mix_data: caseMix } : null
    );
  };

  // Helper to check if primary diagnosis is compliant
  const isPrimaryDiagnosisCompliant = () => {
    if (
      !responseData?.patient_data?.authorized_diagnoses ||
      responseData.patient_data.authorized_diagnoses.length === 0
    ) {
      return false;
    }

    for (const item of responseData.patient_data.authorized_diagnoses) {
      const match = item.authorized_diagnoses.match(/\[(.*?)\]/);
      if (match) {
        const codes = match[1].split(',').map(code => code.trim());
        if (
          responseData.primary_diagnosis &&
          codes.includes(responseData.primary_diagnosis.diagnosis.name)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  // Helper to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = parseISOToLocalDate(dateString);
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Modal handlers
  const handleOpenModal = (dataType: string, data: unknown) => {
    const titleMap: Record<string, string> = {
      home_health_orders: 'Home Health Order',
      face_to_face_visits: 'Face-to-Face Visit Summary',
      history_and_physical: 'History & Physical',
      demographics: 'Demographics',
      past_visit_diagnoses: 'Past Visit Diagnoses',
      narrative: 'Visit Narrative',
      medications: 'Medications',
      wounds: 'Wound Care',
      medical_form: 'Full Visit Data',
      authorized_diagnoses: 'Authorized Diagnoses',
    };

    setModalData({
      title: titleMap[dataType] || 'Medical Data',
      dataType,
      data,
    });
    setModalOpen(true);
  };

  const handleOpenPdf = async (pdfKey: string) => {
    try {
      const blob = await downloadPdf(pdfKey);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to open PDF';
      showError(message);
    }
  };

  // Diagnosis editing handlers
  const handleEditPrimaryDiagnosis = () => {
    setEditingPrimaryDiagnosis(true);
    setAddDiagnosisModalOpen(true);
  };

  const handleEditSecondaryDiagnosis = (index: number) => {
    setEditingDiagnosisIndex(index);
    setAddDiagnosisModalOpen(true);
  };

  const handleRemoveSecondaryDiagnosis = async (index: number) => {
    if (!responseData?.secondary_diagnoses || !responseData?.primary_diagnosis)
      return;

    const updatedSecondaryDiagnoses =
      responseData.secondary_diagnoses.diagnoses.filter((_, i) => i !== index);

    setResponseData(prev =>
      prev
        ? {
            ...prev,
            secondary_diagnoses: {
              reasoning: prev.secondary_diagnoses?.reasoning ?? '',
              diagnoses: updatedSecondaryDiagnoses,
            },
          }
        : null
    );

    try {
      setRecalculating(true);
      await recalculateAndUpdate(
        responseData.primary_diagnosis.diagnosis.name,
        updatedSecondaryDiagnoses.map(d => d.diagnosis.name)
      );
      showSuccess('Diagnosis removed and payment information updated');
    } catch (error) {
      console.error('Error recalculating case mix:', error);
      showError(
        'Diagnosis removed but failed to recalculate payment information'
      );
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddDiagnosis = () => {
    setEditingDiagnosisIndex(null);
    setEditingPrimaryDiagnosis(false);
    setAddDiagnosisModalOpen(true);
  };

  const handleCloseDiagnosisModal = () => {
    setAddDiagnosisModalOpen(false);
    setEditingDiagnosisIndex(null);
    setEditingPrimaryDiagnosis(false);
  };

  const handleReorderDiagnoses = async (
    reorderedDiagnoses: DiagnosisWithReasoning[]
  ) => {
    if (!responseData || !responseData.primary_diagnosis) return;

    setResponseData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        secondary_diagnoses: {
          reasoning: prev.secondary_diagnoses?.reasoning ?? '',
          diagnoses: reorderedDiagnoses,
        },
      };
    });

    try {
      setRecalculating(true);
      await recalculateAndUpdate(
        responseData.primary_diagnosis.diagnosis.name,
        reorderedDiagnoses.map(d => d.diagnosis.name)
      );
      showSuccess('Diagnoses reordered and payment information updated');
    } catch (error) {
      console.error('Error recalculating case mix:', error);
      showError(
        'Diagnoses reordered but failed to recalculate payment information'
      );
    } finally {
      setRecalculating(false);
    }
  };

  // Handle O/E, Date, Severity field changes for primary diagnosis
  const handlePrimaryFieldChange = (fields: {
    oe?: OEType;
    onset_date?: string | null;
    severity?: SeverityType;
  }) => {
    if (!responseData?.primary_diagnosis) return;

    setResponseData(prev => {
      if (!prev || !prev.primary_diagnosis) return prev;
      return {
        ...prev,
        primary_diagnosis: {
          ...prev.primary_diagnosis,
          diagnosis: {
            ...prev.primary_diagnosis.diagnosis,
            oe: fields.oe,
            onset_date: fields.onset_date,
            severity: fields.severity,
          },
        },
      };
    });
  };

  // Handle O/E, Date, Severity field changes for secondary diagnoses
  const handleSecondaryFieldChange = (
    index: number,
    fields: {
      oe?: OEType;
      onset_date?: string | null;
      severity?: SeverityType;
    }
  ) => {
    if (!responseData?.secondary_diagnoses) return;

    setResponseData(prev => {
      if (!prev || !prev.secondary_diagnoses) return prev;
      const updatedDiagnoses = [...prev.secondary_diagnoses.diagnoses];
      updatedDiagnoses[index] = {
        ...updatedDiagnoses[index],
        diagnosis: {
          ...updatedDiagnoses[index].diagnosis,
          oe: fields.oe,
          onset_date: fields.onset_date,
          severity: fields.severity,
        },
      };
      return {
        ...prev,
        secondary_diagnoses: {
          ...prev.secondary_diagnoses,
          diagnoses: updatedDiagnoses,
        },
      };
    });
  };

  const handleConfirmDiagnosis = async (
    newDiagnosis: DiagnosisWithReasoning
  ) => {
    if (
      !responseData ||
      !responseData.primary_diagnosis ||
      !responseData.secondary_diagnoses
    )
      return;

    handleCloseDiagnosisModal();

    try {
      setRecalculating(true);

      // Handle primary diagnosis editing
      if (editingPrimaryDiagnosis) {
        setResponseData(prev =>
          prev
            ? {
                ...prev,
                primary_diagnosis: {
                  diagnosis: newDiagnosis.diagnosis,
                  reasoning: newDiagnosis.reasoning,
                },
              }
            : null
        );

        await recalculateAndUpdate(
          newDiagnosis.diagnosis.name,
          responseData.secondary_diagnoses.diagnoses.map(d => d.diagnosis.name)
        );
        showSuccess('Primary diagnosis and payment information updated');
        return;
      }

      // Handle secondary diagnosis editing
      if (editingDiagnosisIndex !== null) {
        const updatedDiagnoses = [
          ...responseData.secondary_diagnoses.diagnoses,
        ];
        updatedDiagnoses[editingDiagnosisIndex] = newDiagnosis;

        setResponseData(prev =>
          prev
            ? {
                ...prev,
                secondary_diagnoses: {
                  reasoning: responseData.secondary_diagnoses?.reasoning ?? '',
                  diagnoses: updatedDiagnoses,
                },
              }
            : null
        );

        await recalculateAndUpdate(
          responseData.primary_diagnosis.diagnosis.name,
          updatedDiagnoses.map(d => d.diagnosis.name)
        );
        showSuccess('Diagnosis and payment information updated');
        return;
      }

      // Handle adding new secondary diagnosis
      const updatedDiagnoses = [
        ...responseData.secondary_diagnoses.diagnoses,
        newDiagnosis,
      ];

      setResponseData({
        ...responseData,
        secondary_diagnoses: {
          reasoning: responseData.secondary_diagnoses?.reasoning ?? '',
          diagnoses: updatedDiagnoses,
        },
      });

      await recalculateAndUpdate(
        responseData.primary_diagnosis.diagnosis.name,
        updatedDiagnoses.map(d => d.diagnosis.name)
      );
      showSuccess('Diagnosis added and payment information updated');
    } catch (error) {
      console.error('Error recalculating case mix:', error);
      showError(
        'Diagnosis updated but failed to recalculate payment information'
      );
    } finally {
      setRecalculating(false);
    }
  };

  const getCurrentEditingDiagnosis = () => {
    if (!responseData) return null;

    if (editingPrimaryDiagnosis) {
      return responseData.primary_diagnosis ?? null;
    }

    if (editingDiagnosisIndex !== null && responseData.secondary_diagnoses) {
      return responseData.secondary_diagnoses.diagnoses[editingDiagnosisIndex];
    }

    return null;
  };

  const handleBackToSearch = () => {
    if (location.state?.search) {
      navigate({ pathname: '/coding', search: location.state.search });
    } else {
      navigate('/coding');
    }
  };

  // Render processing status
  const renderProcessingStatus = () => {
    if (processingStatus === 'processing' && isPolling) {
      return (
        <Card className='my-6 border-blue-300 bg-blue-50'>
          <CardContent className='p-6 text-center'>
            <div className='flex flex-col items-center gap-4'>
              <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center'>
                <Clock className='w-8 h-8 text-blue-600 animate-pulse' />
              </div>

              <div>
                <h3 className='text-lg font-semibold text-blue-900 mb-2'>
                  Processing Diagnosis Data
                </h3>
                <p className='text-blue-700 mb-4'>
                  We're analyzing the patient data and generating diagnosis
                  codes.
                  <br />
                  This usually takes up to 7 minutes.
                </p>

                <div className='w-24 h-1 bg-blue-200 rounded-full mx-auto overflow-hidden'>
                  <div className='w-full h-full bg-blue-600 rounded-full animate-pulse'></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (processingStatus === 'not_processing') {
      return (
        <Alert className='my-6 border-yellow-200 bg-yellow-50'>
          <AlertTriangle className='h-4 w-4 text-yellow-600' />
          <AlertDescription className='text-yellow-800'>
            No data is available or being processed for this visit. If you
            believe this is an error, please contact Roger support.
          </AlertDescription>
        </Alert>
      );
    }

    if (processingStatus === 'error') {
      return (
        <Alert className='my-6 border-red-200 bg-red-50'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          <AlertDescription className='text-red-800'>
            An error occurred while fetching diagnosis data. Please try again.
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  return (
    <div className='container mx-auto px-4 py-6'>
      <div className='mb-6'>
        <h1 className='text-2xl font-bold text-gray-900'>
          Medical Coding Assistant
        </h1>
      </div>

      {/* Back to Search Button - Always visible */}
      {!responseData && (
        <div className='mb-6'>
          <Button variant='outline' onClick={handleBackToSearch}>
            <ArrowLeft className='w-4 h-4' />
            Back to Search
          </Button>
        </div>
      )}

      {/* Loading State */}
      {loading && !responseData && (
        <div className='flex items-center justify-center min-h-[400px]'>
          <div className='flex flex-col items-center gap-4'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
            <p className='text-muted-foreground'>Loading diagnosis data...</p>
          </div>
        </div>
      )}

      {/* Diagnosis Editing View */}
      {responseData && (
        <>
          <div className='flex items-center justify-between mb-6'>
            <Button variant='outline' onClick={handleBackToSearch}>
              <ArrowLeft className='w-4 h-4' />
              Back to Search
            </Button>

            {/* Diagnosis State Manager */}
            {responseData && lastSavedState && (
              <DiagnosisStateManager
                diagnosisId={
                  selectedVisit?.diagnosis_id ||
                  urlDiagnosisId ||
                  responseData.id
                }
                currentState={{
                  primary_diagnosis: responseData.primary_diagnosis,
                  secondary_diagnoses: responseData.secondary_diagnoses,
                  case_mix_data: responseData.case_mix_data,
                }}
                savedStates={getAllStates()}
                activeStateId={activeStateId}
                onSelectState={handleSelectState}
                onRefresh={handleRefresh}
                onStateUpdated={handleStateUpdated}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            )}
          </div>
          {/* Visit Header - show patient info when available */}
          {(() => {
            const visitInfo = selectedVisit?.visit || responseData?.visit;
            if (!visitInfo && !responseData) return null;

            return (
              <Card className='mb-6 border-primary-200 bg-primary-50'>
                <CardContent>
                  <div className='flex items-center justify-between'>
                    <div className='space-y-2'>
                      <div className='flex items-baseline gap-4'>
                        <h2 className='text-xl font-bold text-primary'>
                          {visitInfo
                            ? `${visitInfo.patient_first_name} ${visitInfo.patient_last_name}`
                            : responseData?.name || 'Patient Information'}
                        </h2>
                        {visitInfo?.patient_id && (
                          <span className='text-xs text-gray-500 font-medium'>
                            MRN: {visitInfo.patient_id}
                          </span>
                        )}
                      </div>
                      {/* Tags */}
                      <div className='flex items-center gap-3'>
                        <div className='flex items-center gap-1.5'>
                          <Upload
                            className={`w-3.5 h-3.5 ${responseData?.tags?.EMR_filled ? 'text-green-500' : 'text-gray-400'}`}
                          />
                          <span className='text-xs text-gray-500'>
                            Filled into EMR:{' '}
                            {responseData?.tags?.EMR_filled ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className='flex items-center gap-1.5'>
                          <Archive
                            className={`w-3.5 h-3.5 ${responseData?.tags?.archived ? 'text-orange-500' : 'text-gray-400'}`}
                          />
                          <span className='text-xs text-gray-500'>
                            Archived:{' '}
                            {responseData?.tags?.archived ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {visitInfo && (
                      <div className='flex items-center gap-6'>
                        <div className='flex items-center gap-2 text-gray-700'>
                          <Calendar className='w-4 h-4 text-gray-500' />
                          <span className='text-sm font-medium'>
                            {formatDate(visitInfo.visit_date_date)}
                          </span>
                        </div>
                        {(visitInfo.nurse_first_name ||
                          visitInfo.nurse_last_name) && (
                          <div className='flex items-center gap-2 text-gray-700'>
                            <User className='w-4 h-4 text-gray-500' />
                            <span className='text-sm font-medium'>
                              {visitInfo.nurse_first_name}{' '}
                              {visitInfo.nurse_last_name}
                            </span>
                          </div>
                        )}
                        <div className='flex items-center gap-2'>
                          <Badge>{visitInfo.visit_type}</Badge>
                        </div>
                        <CodingActionsMenu
                          diagnosisIds={
                            selectedVisit?.diagnosis_id
                              ? [selectedVisit.diagnosis_id]
                              : urlDiagnosisId
                                ? [urlDiagnosisId]
                                : responseData?.id
                                  ? [responseData.id]
                                  : []
                          }
                          onActionComplete={handleRefresh}
                          trigger={
                            <Button
                              variant='outline'
                              size='sm'
                              className='h-7 border-primary-200 text-primary hover:bg-primary-100'
                            >
                              <Command className='w-3.5 h-3.5 mr-2' />
                              Actions
                              <ChevronDown className='w-3.5 h-3.5 ml-2' />
                            </Button>
                          }
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Push to EMR Card */}
          {responseData && (
            <PushToEmrCard
              diagnosisId={
                selectedVisit?.diagnosis_id || urlDiagnosisId || responseData.id
              }
              manualSaveStateId={
                activeStateId == 'original'
                  ? undefined
                  : activeStateId || undefined
              }
              savedStates={getAllStates()}
              onStateCreated={handleRefresh}
              currentState={responseData}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          )}
        </>
      )}

      {/* Show processing status */}
      {renderProcessingStatus()}

      {/* Only show coding results after data has been successfully fetched */}
      {responseData && (
        <div className='mt-6 space-y-6'>
          {/* Patient Medical Data */}
          {responseData.patient_data && (
            <PatientInformationSection
              medicalData={responseData.patient_data}
              onOpenModal={handleOpenModal}
              handleOpenPdf={handleOpenPdf}
              showTranscription={false}
            />
          )}

          {/* Case Mix Data */}
          {responseData.case_mix_data && (
            <Card>
              <CardHeader>
                <CardTitleWithIcon
                  icon={Calculator}
                  title='Payment Information'
                  subtitle='Case mix and reimbursement details'
                />
              </CardHeader>
              <CardContent>
                <CaseMixCard
                  caseMixData={responseData.case_mix_data}
                  isRecalculating={recalculating}
                />
              </CardContent>
            </Card>
          )}

          {/* Post-Coding Checks - only show for original state */}
          {(() => {
            // Only show post-coding checks for the original state
            if (activeStateId !== 'original') return null;
            if (!responseData.post_coding_checks) return null;

            const activeChecks = Object.values(
              responseData.post_coding_checks
            ).filter(check => check.active);
            if (activeChecks.length === 0) return null;

            return (
              <Card>
                <CardHeader>
                  <CardTitleWithIcon
                    icon={AlertTriangle}
                    title='Post-Coding Checks'
                    subtitle='Quality assurance and compliance validation'
                  />
                </CardHeader>
                <CardContent>
                  <PostCodingChecksCard
                    checks={responseData.post_coding_checks}
                    onOpenModal={dataType => {
                      const data =
                        responseData.patient_data?.[
                          dataType as keyof typeof responseData.patient_data
                        ];
                      handleOpenModal(dataType, data);
                    }}
                  />
                </CardContent>
              </Card>
            );
          })()}

          {/* Rerun Coding Button for Cancelled Visits */}
          {(() => {
            // Check if visit status is CANCELLED using raw_status
            if (responseData.raw_status !== 'CANCELLED') return null;

            return (
              <Card className='border-orange-200 bg-orange-50'>
                <CardHeader>
                  <CardTitleWithIcon
                    icon={AlertTriangle}
                    title='Visit Cancelled'
                    subtitle='Medical coding was cancelled for this visit'
                  />
                </CardHeader>
                <CardContent>
                  <div className='flex items-center justify-between'>
                    <p className='text-sm text-orange-800'>
                      Once you resolved the mentioned issues in the EMR, you can
                      restart the medical coding process for this visit. This
                      will create a new coding entry for the patient and you
                      will lose access to this page.
                    </p>
                    <Button
                      onClick={handleRerunClick}
                      className='bg-orange-600 hover:bg-orange-700 text-white p-3'
                    >
                      <RefreshCw className='w-4 h-4 mr-2' />
                      Rerun Coding
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Primary Diagnosis */}
          {responseData.primary_diagnosis && (
            <Card>
              <CardHeader>
                <CardTitleWithIcon
                  icon={Stethoscope}
                  title='Primary Diagnosis'
                  subtitle='Principal diagnostic code'
                />
              </CardHeader>
              <CardContent>
                <DiagnosisCard
                  diagnosis={responseData.primary_diagnosis}
                  variant='primary'
                  onEdit={handleEditPrimaryDiagnosis}
                  isCompliant={isPrimaryDiagnosisCompliant()}
                  onFieldChange={handlePrimaryFieldChange}
                  onOpenModal={dataType => {
                    const data =
                      responseData.patient_data?.[
                        dataType as keyof typeof responseData.patient_data
                      ];
                    handleOpenModal(dataType, data);
                  }}
                />

                {/* Alternative Primary Codes */}
                {(() => {
                  if (activeStateId !== 'original') return null;
                  if (
                    !responseData.valid_primary_diagnoses ||
                    responseData.valid_primary_diagnoses.length === 0
                  )
                    return null;

                  return (
                    <>
                      <Alert
                        className='mt-6 border-blue-200 bg-blue-50 flex items-center justify-between cursor-pointer hover:bg-blue-100 transition-colors'
                        onClick={() => setAlternativePrimaryCodesOpen(true)}
                      >
                        <div className='flex items-center gap-3'>
                          <div className='p-2 bg-blue-100 rounded-full'>
                            <Stethoscope className='h-5 w-5 text-blue-600' />
                          </div>
                          <div>
                            <h4 className='text-sm font-bold text-blue-900'>
                              Alternative Primary Codes
                            </h4>
                            <AlertDescription className='text-blue-800 text-xs mt-0.5'>
                              View {responseData.valid_primary_diagnoses.length}{' '}
                              alternative codes identified for this visit
                            </AlertDescription>
                          </div>
                        </div>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='text-blue-700 hover:text-blue-900 hover:bg-blue-200 shrink-0'
                        >
                          View Alternatives
                        </Button>
                      </Alert>

                      <Dialog
                        open={alternativePrimaryCodesOpen}
                        onOpenChange={setAlternativePrimaryCodesOpen}
                      >
                        <DialogContent className='max-w-3xl max-h-[80vh] flex flex-col'>
                          <DialogHeader>
                            <DialogTitle>Alternative Primary Codes</DialogTitle>
                          </DialogHeader>
                          <div className='flex-1 overflow-y-auto pr-2 space-y-4 p-1'>
                            {responseData.valid_primary_diagnoses.map(
                              (dx, idx) => {
                                // Check compliance
                                let isCompliant = false;
                                const authorizedDiagnoses =
                                  responseData.patient_data
                                    ?.authorized_diagnoses;

                                if (authorizedDiagnoses) {
                                  for (const item of authorizedDiagnoses) {
                                    const match =
                                      item.authorized_diagnoses.match(
                                        /\[(.*?)\]/
                                      );
                                    if (match) {
                                      const codes = match[1]
                                        .split(',')
                                        .map(code => code.trim());
                                      if (codes.includes(dx.diagnosis.name)) {
                                        isCompliant = true;
                                        break;
                                      }
                                    }
                                  }
                                }

                                return (
                                  <Card
                                    key={idx}
                                    className='border-l-4 border-l-blue-500 shadow-sm'
                                  >
                                    <CardContent className='pt-6'>
                                      <DiagnosisCard
                                        diagnosis={dx}
                                        variant='primary'
                                        isCompliant={isCompliant}
                                        onOpenModal={dataType => {
                                          const data =
                                            responseData.patient_data?.[
                                              dataType as keyof typeof responseData.patient_data
                                            ];
                                          handleOpenModal(dataType, data);
                                        }}
                                      />
                                    </CardContent>
                                  </Card>
                                );
                              }
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Secondary Diagnoses */}
          {responseData.secondary_diagnoses && (
            <Card>
              <CardHeader>
                <CardTitleWithIcon
                  icon={List}
                  title={`Secondary Diagnoses (${responseData.secondary_diagnoses?.diagnoses?.length})`}
                  subtitle='Additional diagnostic codes'
                />
              </CardHeader>
              <CardContent>
                {/* Diagnosis Cards in 3-column grid with drag and drop */}
                <div className='mb-6'>
                  <DraggableGrid
                    items={responseData.secondary_diagnoses?.diagnoses || []}
                    onReorder={handleReorderDiagnoses}
                    renderItem={(dx, idx) => (
                      <SortableDiagnosisCard
                        id={`diagnosis-${idx}`}
                        diagnosis={dx}
                        variant='secondary'
                        position={idx + 1}
                        onEdit={() => handleEditSecondaryDiagnosis(idx)}
                        onRemove={() => handleRemoveSecondaryDiagnosis(idx)}
                        onFieldChange={fields =>
                          handleSecondaryFieldChange(idx, fields)
                        }
                        onOpenModal={dataType => {
                          const data =
                            responseData.patient_data?.[
                              dataType as keyof typeof responseData.patient_data
                            ];
                          handleOpenModal(dataType, data);
                        }}
                      />
                    )}
                    getItemId={(_, idx) => `diagnosis-${idx}`}
                    gridClassName='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                    sortingStrategy='rect'
                  >
                    {/* Add Diagnosis Card */}
                    <div
                      onClick={handleAddDiagnosis}
                      className='bg-gray-50 border-2 border-dashed border-primary rounded-lg p-6 flex flex-col items-center justify-center gap-3 hover:bg-primary-50 hover:border-primary transition-colors min-h-[120px] cursor-pointer'
                    >
                      <div className='w-12 h-12 rounded-full bg-primary flex items-center justify-center'>
                        <Plus className='w-6 h-6 text-white' />
                      </div>
                      <span className='text-primary font-semibold text-sm'>
                        Add Diagnosis
                      </span>
                      <span className='text-xs text-gray-500'>
                        Click to add new code
                      </span>
                    </div>
                  </DraggableGrid>
                </div>

                {/* Overall Reasoning */}
                {responseData.secondary_diagnoses?.reasoning && (
                  <Accordion type='single' collapsible>
                    <AccordionItem value='item-1'>
                      <AccordionTrigger className='text-xs text-muted-foreground'>
                        Coding Strategy & Considerations
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className='mt-4'>
                          <div className='text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2'></div>
                          <ClickableTextWithModals
                            text={responseData.secondary_diagnoses?.reasoning}
                            onOpenModal={dataType => {
                              const data =
                                responseData.patient_data?.[
                                  dataType as keyof typeof responseData.patient_data
                                ];
                              handleOpenModal(dataType, data);
                            }}
                            size='sm'
                            className='text-gray-700'
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Medical Data Modal */}
      {modalData && responseData && (
        <MedicalDataModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          title={modalData.title}
          dataType={modalData.dataType}
          data={modalData.data}
          pdfPaths={
            Array.isArray(responseData.patient_data?.pdf_paths)
              ? responseData.patient_data?.pdf_paths
              : responseData.patient_data?.pdf_paths?.intake_documents
          }
          onOpenPdf={handleOpenPdf}
          onOpenNestedModal={dataType => {
            const data =
              responseData.patient_data?.[
                dataType as keyof typeof responseData.patient_data
              ];
            handleOpenModal(dataType, data);
          }}
        />
      )}

      {/* Add/Edit Diagnosis Modal */}
      <AddDiagnosisModal
        open={addDiagnosisModalOpen}
        onClose={handleCloseDiagnosisModal}
        onConfirm={handleConfirmDiagnosis}
        currentDiagnosis={getCurrentEditingDiagnosis()}
        isEditing={editingPrimaryDiagnosis || editingDiagnosisIndex !== null}
        isPrimaryDiagnosis={editingPrimaryDiagnosis}
        diagnosisPosition={
          editingPrimaryDiagnosis
            ? 'Primary'
            : editingDiagnosisIndex !== null
              ? editingDiagnosisIndex + 1
              : null
        }
      />

      <Dialog open={rerunModalOpen} onOpenChange={handleCloseRerunModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rerun Medical Coding</DialogTitle>
          </DialogHeader>

          <div className='py-4'>
            {responseData && !rerunResult && (
              <div className='space-y-4'>
                <p className='text-gray-600'>
                  Are you sure you want to rerun medical coding for this visit?
                </p>
                <div className='bg-blue-50 p-3 rounded-md border border-blue-100 text-sm text-blue-800 flex gap-2'>
                  <Info className='w-4 h-4 mt-0.5 flex-shrink-0' />
                  <span>
                    This will start a new coding workflow for this visit.
                  </span>
                </div>
              </div>
            )}

            {rerunResult && !rerunResult.success && (
              <Alert className='border-red-200 bg-red-50'>
                <AlertCircle className='h-4 w-4 text-red-600' />
                <AlertDescription className='text-red-800'>
                  {rerunResult.message}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            {!rerunResult && (
              <>
                <Button
                  variant='outline'
                  onClick={handleCloseRerunModal}
                  disabled={rerunLoading}
                >
                  Cancel
                </Button>
                <Button onClick={handleConfirmRerun} disabled={rerunLoading}>
                  {rerunLoading && (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  )}
                  Rerun Coding
                </Button>
              </>
            )}
            {rerunResult && !rerunResult.success && (
              <Button onClick={handleCloseRerunModal}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CodingDiagnosis;
